import React, { useState, useRef } from 'react';
import './AgentPlatformModal.css';
import './InferenceModal.css';

function InferenceModal({ closeModal, addQuoteItem, formatPrice, plansAgentPlatform, plansDroplets }) {
  const [activeTab, setActiveTab] = useState('Serverless Inference');
  const [name, setName] = useState('');

  // Serverless Inference state
  const [searchTerm, setSearchTerm] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputTokens, setInputTokens] = useState('');
  const [outputTokens, setOutputTokens] = useState('');

  // Dedicated Inference state
  const [diSearchTerm, setDiSearchTerm] = useState('');
  const [diShowDropdown, setDiShowDropdown] = useState(false);
  const [diSelectedPlan, setDiSelectedPlan] = useState(null);
  const [gpuHours, setGpuHours] = useState(744);
  const [diQuantity, setDiQuantity] = useState(1);

  const minGpuHours = 1;
  const maxGpuHours = 744;

  const searchInputRef = useRef(null);
  const quantityInputRef = useRef(null);

  // Serverless Inference derived values
  const inputTokenCost = selectedPlan && inputTokens > 0 ? inputTokens * selectedPlan['Rate Input'] : 0;
  const outputTokenCost = selectedPlan && outputTokens > 0 ? outputTokens * selectedPlan['Rate Output'] : 0;

  // Serverless Inference handlers
  const handleAddServerless = () => {
    const description = `Serverless Inference model: ${selectedPlan?.Description || ''}.`;
    const features = `Includes ${
      inputTokens >= 1000000
        ? `${(inputTokens / 1000000).toLocaleString()} Trillion`
        : inputTokens >= 1000
          ? `${(inputTokens / 1000).toLocaleString()} Billion`
          : `${inputTokens.toLocaleString()} Million`
    } input tokens and ${
      outputTokens >= 1000000
        ? `${(outputTokens / 1000000).toLocaleString()} Trillion`
        : outputTokens >= 1000
          ? `${(outputTokens / 1000).toLocaleString()} Billion`
          : `${outputTokens.toLocaleString()} Million`
    } output tokens per month.`;
    const priceMonthly = inputTokenCost + outputTokenCost;

    addQuoteItem({
      serviceType: 'Serverless Inference',
      name: name,
      description: description,
      features: features,
      priceMonthly: formatPrice(priceMonthly),
      quantity: quantity,
      totalPrice: formatPrice(priceMonthly * quantity),
    });
    closeModal();
  };

  // Dedicated Inference handlers
  const calculateDiPrice = () => {
    if (!diSelectedPlan || !diSelectedPlan.di) return 0;
    return parseFloat(diSelectedPlan.di) * gpuHours * parseInt(diSelectedPlan.GPU);
  };

  const handleAddDedicated = () => {
    const priceMonthly = calculateDiPrice();
    const description = `${diSelectedPlan.Description} - ${diSelectedPlan.VCPUs} vCPU / ${diSelectedPlan.Memory} GiB Memory / ${diSelectedPlan.Disk} GiB Boot Disk.`;
    const features = `Dedicated Inference with ${gpuHours} hour${gpuHours === 1 ? '' : 's'} of usage per month at $${diSelectedPlan.di} per GPU.`;

    addQuoteItem({
      serviceType: 'Dedicated Inference',
      name: name,
      description: description,
      features: features,
      priceMonthly: formatPrice(priceMonthly),
      quantity: diQuantity,
      totalPrice: formatPrice(priceMonthly * diQuantity),
    });
    closeModal();
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setShowDropdown(false);
    setDiShowDropdown(false);
  };

  // Filtered plans
  const filteredSIPlans = plansAgentPlatform.filter(plan => {
    const regex = new RegExp(searchTerm.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
    return plan.Subservice === 'Serverless Inference' && regex.test(plan.Description);
  });

  const filteredDiPlans = plansDroplets.filter(plan => {
    if (!plan.GPU || !plan.di) return false;
    const regex = new RegExp(diSearchTerm.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
    return (
      regex.test(plan.Description) ||
      regex.test(plan.Slug) ||
      regex.test(`${plan.VCPUs} vCPU`) ||
      regex.test(`${plan.Memory} GB Memory`)
    );
  });

  // Serverless Inference plan/quantity handlers
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowDropdown(false);
    setSearchTerm('');
    setQuantity(1);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 1 && Number(value) <= 1000)) {
      setQuantity(value);
    }
  };

  const handleQuantityBlur = () => {
    if (quantity === '' || quantity < 1) {
      setQuantity(1);
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
    }
  };

  // Dedicated Inference plan/quantity handlers
  const handleDiPlanSelect = (plan) => {
    setDiSelectedPlan(plan);
    setDiShowDropdown(false);
    setDiSearchTerm('');
    setGpuHours(744);
    setDiQuantity(1);
  };

  const handleDiQuantityChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 1 && Number(value) <= 65000)) {
      setDiQuantity(value);
    }
  };

  const handleDiQuantityBlur = () => {
    if (diQuantity === '' || diQuantity < 1) setDiQuantity(1);
  };

  const handleGpuHoursIncrement = () => setGpuHours(h => Math.min(h + 1, maxGpuHours));
  const handleGpuHoursDecrement = () => setGpuHours(h => Math.max(h - 1, minGpuHours));

  return (
    <div id="modal" className="modal agentplatform-modal inference-modal">
      <div id="modal-content" className="modal-content">
        <span id="close" className="close" onClick={closeModal}>&times;</span>
        <div id="modal-header" className="modal-header flex flex-between flex-center">
          <div>
            <h1>Add Inference</h1>
          </div>
          <div id="modal-item-name" className="modal-item-name">
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter item name or description"
              maxLength="50"
            />
          </div>
        </div>
        <div id="modal-tabs" className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'Serverless Inference' ? 'tab-button-active' : ''}`}
            onClick={() => handleTabClick('Serverless Inference')}
          >
            Serverless Inference
          </button>
          <button
            className={`tab-button ${activeTab === 'Dedicated Inference' ? 'tab-button-active' : ''}`}
            onClick={() => handleTabClick('Dedicated Inference')}
          >
            Dedicated Inference
          </button>
        </div>

        <div id="agentplatform-modal-body" className="modal-body">

          {activeTab === 'Serverless Inference' && (
            <div>
              <h5>SELECT MODEL</h5>
              <div id="search-container" className="search-container">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="input"
                  placeholder="Search plans or click to view all"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
                  onClick={() => setShowDropdown(!showDropdown)}
                />
                {showDropdown && filteredSIPlans.length > 0 && (
                  <ul id="dropdown-agentplatform" className="dropdown">
                    {filteredSIPlans.map((plan, index) => (
                      <li key={index} onClick={() => handlePlanSelect(plan)} className="flex font-darkGray">
                        <div className="dropdown-plans-description">{plan.Description}</div>
                        <div id="price" className="dropdown-plans-rate-input">
                          <span className="dollar">$</span>
                          <span>{formatPrice(plan['Rate Input'])}</span>
                          <span className="period">/1M input</span>
                        </div>
                        <div id="price" className="dropdown-plans-rate-output">
                          <span className="dollar">$</span>
                          <span>{formatPrice(plan['Rate Output'])}</span>
                          <span className="period">/1M output</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {showDropdown && filteredSIPlans.length === 0 && (
                  <ul id="dropdown" className="dropdown"><li>No plans found</li></ul>
                )}
              </div>
              {selectedPlan && (
                <div>
                  <div id="card" className="card">
                    <div id="card-header" className="card-header flex flex-between">
                      <div><h5>SELECTED MODEL</h5></div>
                      <div id="price" className="price flex-column">
                        <div>
                          <span className="dollar">$</span>
                          <span className="cost">{formatPrice(selectedPlan['Rate Input'])}</span>
                          <span className="period">/1M input</span>
                        </div>
                        <div>
                          <span className="dollar">$</span>
                          <span className="cost">{formatPrice(selectedPlan['Rate Output'])}</span>
                          <span className="period">/1M output</span>
                        </div>
                      </div>
                    </div>
                    <div id="card-body" className="card-body"><p>{selectedPlan.Description}</p></div>
                  </div>
                  <h5>SELECT INPUT TOKENS</h5>
                  <div id="calculation-container" className="calculation-container">
                    <div id="calculation-container-price" className="calculation-container-price flex">
                      <div className="price">
                        <span className="dollar dollar-large">$</span>
                        <span className="cost cost-large">{inputTokenCost ? formatPrice(inputTokenCost) : '0'}</span>
                        <span className="period period-large">/mo</span>
                      </div>
                      <div>
                        <span>
                          {inputTokens === "" ? "_ Million tokens"
                            : inputTokens >= 1000000 ? `${(inputTokens / 1000000).toFixed(2).replace(/\.0+$/, '')} Trillion tokens`
                            : inputTokens >= 1000 ? `${(inputTokens / 1000).toFixed(2).replace(/\.0+$/, '')} Billion tokens`
                            : `${inputTokens} Million tokens`}
                        </span>
                      </div>
                    </div>
                    <div id="calculation-container-input" className="calculation-container-input">
                      <input
                        type="text" spellCheck="false" required placeholder="Enter input tokens"
                        value={inputTokens}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0 && numValue <= 1000000) setInputTokens(value);
                            else if (value === '') setInputTokens('');
                          }
                        }}
                      />
                      <div id="floatlabel-price" className="floatlabel-price">
                        <label>Enter input tokens in Millions</label>
                      </div>
                    </div>
                  </div>
                  <h5>SELECT OUTPUT TOKENS</h5>
                  <div id="calculation-container-output" className="calculation-container">
                    <div id="calculation-container-price" className="calculation-container-price flex">
                      <div className="price">
                        <span className="dollar dollar-large">$</span>
                        <span className="cost cost-large">{outputTokenCost ? formatPrice(outputTokenCost) : '0'}</span>
                        <span className="period period-large">/mo</span>
                      </div>
                      <div>
                        <span>
                          {outputTokens === "" ? "_ Million tokens"
                            : outputTokens >= 1000000 ? `${(outputTokens / 1000000).toFixed(2).replace(/\.0+$/, '')} Trillion tokens`
                            : outputTokens >= 1000 ? `${(outputTokens / 1000).toFixed(2).replace(/\.0+$/, '')} Billion tokens`
                            : `${outputTokens} Million tokens`}
                        </span>
                      </div>
                    </div>
                    <div id="calculation-container-input" className="calculation-container-input">
                      <input
                        type="text" spellCheck="false" required placeholder="Enter output tokens"
                        value={outputTokens}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0 && numValue <= 1000000) setOutputTokens(value);
                            else if (value === '') setOutputTokens('');
                          }
                        }}
                      />
                      <div id="floatlabel-price" className="floatlabel-price">
                        <label>Enter output tokens in Millions</label>
                      </div>
                    </div>
                  </div>
                  <h5>SELECT QUANTITY</h5>
                  <input
                    ref={quantityInputRef}
                    type="number" className="input" value={quantity}
                    onChange={handleQuantityChange} onBlur={handleQuantityBlur}
                    min="1" max="1000"
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'Dedicated Inference' && (
            <div>
              <h5>SELECT GPU PLAN</h5>
              <div id="search-container" className="search-container font-darkGray">
                <input
                  ref={searchInputRef}
                  type="text" className="input"
                  placeholder="Search plans or click to view all"
                  value={diSearchTerm}
                  onChange={(e) => { setDiSearchTerm(e.target.value); setDiShowDropdown(true); }}
                  onClick={() => setDiShowDropdown(!diShowDropdown)}
                />
                {diShowDropdown && filteredDiPlans.length > 0 && (
                  <ul id="dropdown" className="dropdown">
                    {filteredDiPlans.map((plan, index) => (
                      <li key={index} onClick={() => handleDiPlanSelect(plan)} className="flex font-darkGray">
                        <div className="dropdown-plans-description">{plan.Description}</div>
                        <div className="dropdown-plans-specifications">
                          {plan.VCPUs} vCPU, {plan.Memory} GB Memory, {plan.Disk} GiB Boot Disk
                        </div>
                        <div id="price" className="price">
                          <span className="dollar">Per GPU $</span>
                          <span>{formatPrice(plan.di)}</span>
                          <span className="period">/hr</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {diShowDropdown && filteredDiPlans.length === 0 && (
                  <ul id="dropdown" className="dropdown"><li className="font-darkGray">No plans found</li></ul>
                )}
              </div>
              {diSelectedPlan && (
                <div>
                  <div id="card" className="card">
                    <div id="card-header" className="card-header flex flex-between flex-center">
                      <div><h5>SELECTED PLAN</h5></div>
                      <div id="price" className="price">
                        <span className="dollar">Per GPU $</span>
                        <span className="cost">{formatPrice(diSelectedPlan.di)}</span>
                        <span className="period">/hr</span>
                      </div>
                    </div>
                    <div id="card-body" className="card-body">
                      <p>{diSelectedPlan.Description}</p>
                      <p>{diSelectedPlan.VCPUs} vCPU / {diSelectedPlan.Memory} GiB Memory / {diSelectedPlan.Disk} GiB Boot Disk</p>
                    </div>
                  </div>
                  <h5>SELECT HOURS PER MONTH</h5>
                  <div className="flex flex-start flex-center gap-2">
                    <div className="stepper-container">
                      <div className="stepper-input-wrapper">
                        <div className="stepper-input">
                          <input
                            type="number" className="stepper-input-field" name="gpuHours-count"
                            placeholder="" min={minGpuHours} max={maxGpuHours} step="10"
                            aria-invalid="false" autoComplete="off" spellCheck="false"
                            value={gpuHours}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') { setGpuHours(''); return; }
                              const n = parseInt(value, 10);
                              if (!isNaN(n) && n <= maxGpuHours) setGpuHours(n);
                            }}
                            onBlur={(e) => {
                              const value = parseInt(e.target.value, 10);
                              if (isNaN(value) || value <= minGpuHours) setGpuHours(minGpuHours);
                              else if (value >= maxGpuHours) setGpuHours(maxGpuHours);
                              else setGpuHours(value);
                            }}
                          />
                        </div>
                      </div>
                      <div className="stepper-button-wrapper-up">
                        <button className="stepper-button-increment" onClick={handleGpuHoursIncrement} disabled={gpuHours === maxGpuHours}>
                          <svg className="stepper-icon-up" aria-hidden="true"><use href="#_sprite-arrow-up"></use></svg>
                        </button>
                      </div>
                      <div className="stepper-button-wrapper-down">
                        <button className="stepper-button-decrement" onClick={handleGpuHoursDecrement} disabled={gpuHours === minGpuHours}>
                          <svg className="stepper-icon-down" aria-hidden="true"><use href="#_sprite-arrow-down"></use></svg>
                        </button>
                      </div>
                    </div>
                    <div className='font-size-dot8'>
                      <p className="font-weight-600">Hour Range</p>
                      <p>{minGpuHours} - {maxGpuHours}</p>
                    </div>
                  </div>
                  <h5>SELECT QUANTITY</h5>
                  <input
                    type="number" className="input" value={diQuantity}
                    onChange={handleDiQuantityChange} onBlur={handleDiQuantityBlur}
                    min="1" max="65000"
                  />
                </div>
              )}
            </div>
          )}

        </div>

        <div id="modal-footer" className="modal-footer flex flex-between flex-center">
          <div id="action-button" className="action-button">
            {activeTab === 'Serverless Inference' && (
              <button
                className="button-blue"
                onClick={handleAddServerless}
                disabled={!selectedPlan || !(Number(inputTokens) > 0 && Number(outputTokens) > 0)}
              >
                Add Serverless Inference
              </button>
            )}
            {activeTab === 'Dedicated Inference' && (
              <button
                className="button-blue"
                onClick={handleAddDedicated}
                disabled={!diSelectedPlan || !(gpuHours > 0)}
              >
                Add Dedicated Inference
              </button>
            )}
          </div>
          <div id="price" className="price">
            <span className="dollar dollar-large">$</span>
            <span className="cost cost-large">
              {formatPrice(
                activeTab === 'Serverless Inference' && selectedPlan
                  ? ((inputTokens > 0 ? inputTokens * selectedPlan['Rate Input'] * quantity : 0) +
                     (outputTokens > 0 ? outputTokens * selectedPlan['Rate Output'] * quantity : 0))
                  : activeTab === 'Dedicated Inference' && diSelectedPlan
                    ? calculateDiPrice() * diQuantity
                    : 0
              )}
            </span>
            <span className="period period-large">/mo</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InferenceModal;
